import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";

import { Button } from "@/components/ui/button";

import { downloadInventory, recommendedDownload, type DownloadItem } from "../config/downloads";
import { siteConfig } from "../config/site";
import { detectPlatform, type DetectedPlatform } from "../lib/platform";

function Rule() {
  return <div className="h-px w-full bg-border/60" />;
}

function DownloadBadge(props: { item: DownloadItem }) {
  if (props.item.comingSoon) {
    return (
      <span className="rounded-full border border-border/60 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
        coming soon
      </span>
    );
  }

  switch (props.item.badge) {
    case "app-store":
      return (
        <span className="rounded-full border border-border/60 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
          app store
        </span>
      );
    case "play-store":
      return (
        <span className="rounded-full border border-border/60 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
          google play
        </span>
      );
    case "f-droid":
      return (
        <span className="rounded-full border border-border/60 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
          f-droid
        </span>
      );
    default:
      return null;
  }
}

export function DownloadPage() {
  const [platform, setPlatform] = useState<DetectedPlatform>("unknown");

  useEffect(() => {
    setPlatform(detectPlatform());
  }, []);

  const recommended = useMemo(() => recommendedDownload(platform), [platform]);

  return (
    <>
      <Helmet>
        <title>Download · {siteConfig.name}</title>
        <meta name="description" content={`Download ${siteConfig.name} for your platform.`} />
      </Helmet>

      <section className="space-y-6 pb-10">
        <div className="space-y-3">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">download</p>
          <h1 className="font-display text-4xl tracking-tight sm:text-5xl">Everything in one place.</h1>
          <p className="max-w-2xl text-muted-foreground">
            We pick a default download based on your platform. All channels, repos, and builds are listed below.
          </p>
        </div>

        <div className="rounded-2xl border border-border/60 p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">recommended</p>
              <div className="flex flex-wrap items-center gap-3">
                <p className="font-display text-2xl tracking-tight">{recommended.label}</p>
                <DownloadBadge item={recommended} />
              </div>
              <p className="text-sm text-muted-foreground">{recommended.description}</p>
            </div>

            {recommended.href ? (
              <Button asChild className="rounded-full px-6">
                <a href={recommended.href} target="_blank" rel="noreferrer">
                  Download
                </a>
              </Button>
            ) : (
              <Button className="rounded-full px-6" disabled>
                Coming soon
              </Button>
            )}
          </div>
        </div>

        <p className="text-sm text-muted-foreground">
          Tip: verify checksums and signatures for release artifacts. If something looks off, report it in{" "}
          <a className="underline underline-offset-4 hover:opacity-80" href={siteConfig.links.issues} target="_blank" rel="noreferrer">
            the issue tracker
          </a>
          .
        </p>
      </section>

      <Rule />

      <section className="space-y-10 py-10">
        <div className="space-y-2">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">all links</p>
          <h2 className="font-display text-2xl tracking-tight">Downloads, repos, and surfaces.</h2>
        </div>

        {downloadInventory.map((section) => (
          <div key={section.heading} className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">{section.heading}</h3>
              <div className="h-px flex-1 bg-border/60" />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {section.items.map((item) => {
                const content = (
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-3">
                        <p className="font-display text-lg tracking-tight">{item.label}</p>
                        <DownloadBadge item={item} />
                      </div>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    </div>
                    {item.qrCodeImageSrc ? (
                      <div className="flex shrink-0 flex-col items-end gap-2">
                        <img
                          src={item.qrCodeImageSrc}
                          alt={item.qrCodeAlt ?? `${item.label} QR code`}
                          className="h-20 w-20 rounded-md border border-border/60 bg-white p-1"
                          loading="lazy"
                        />
                        <span className="max-w-20 text-right font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                          {item.qrCodeHint ?? "scan"}
                        </span>
                      </div>
                    ) : (
                      <span className="font-mono text-muted-foreground">{item.href ? "↗" : "·"}</span>
                    )}
                  </div>
                );

                if (!item.href) {
                  return (
                    <div
                      key={item.id}
                      className="rounded-2xl border border-border/60 bg-muted/20 p-5 opacity-70"
                      aria-disabled="true"
                    >
                      {content}
                    </div>
                  );
                }

                return (
                  <a
                    key={item.id}
                    href={item.href}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-2xl border border-border/60 p-5 transition-colors hover:bg-muted/40"
                  >
                    {content}
                  </a>
                );
              })}
            </div>
          </div>
        ))}
      </section>
    </>
  );
}
